import React, { useState, useRef, useEffect, useCallback } from 'react'
import { LuTableProperties, LuFileText } from 'react-icons/lu'
import { FaChevronLeft, FaTrash } from "react-icons/fa";
import { Alert, Snackbar } from "@mui/material";
import {
    fetchCandidatesByJobId,
    uploadPdfCandidateFile,
} from "../../Redux/ActionCreators/candidateAction";
import { updateJob, uploadResumesFromUrls } from "../../Redux/ActionCreators/jobsAction";
import { readDirectoryFiles } from "../Candidates/AddCandidatesModal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

const RESUME_SCORING_STAGE_ID = "cf1bb4ea-355b-4861-ad7b-ba52d3ce6b82";
const DEFAULT_RESUME_URL = "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314110652_resume_human_resources_manager.pdf";
const DEFAULT_RESUME_FILENAME = "resume_human_resources_manager.pdf";

const COUNTRY_CODES = [
    { code: "IN", dialCode: "+91",  name: "India" },
    { code: "US", dialCode: "+1",   name: "United States" },
    { code: "GB", dialCode: "+44",  name: "United Kingdom" },
    { code: "AU", dialCode: "+61",  name: "Australia" },
    { code: "CA", dialCode: "+1",   name: "Canada" },
    { code: "AE", dialCode: "+971", name: "UAE" },
    { code: "SG", dialCode: "+65",  name: "Singapore" },
    { code: "DE", dialCode: "+49",  name: "Germany" },
    { code: "FR", dialCode: "+33",  name: "France" },
    { code: "NL", dialCode: "+31",  name: "Netherlands" },
    { code: "JP", dialCode: "+81",  name: "Japan" },
    { code: "CN", dialCode: "+86",  name: "China" },
    { code: "BR", dialCode: "+55",  name: "Brazil" },
    { code: "ZA", dialCode: "+27",  name: "South Africa" },
    { code: "NG", dialCode: "+234", name: "Nigeria" },
    { code: "KE", dialCode: "+254", name: "Kenya" },
    { code: "PK", dialCode: "+92",  name: "Pakistan" },
    { code: "BD", dialCode: "+880", name: "Bangladesh" },
    { code: "MY", dialCode: "+60",  name: "Malaysia" },
    { code: "PH", dialCode: "+63",  name: "Philippines" },
    { code: "ID", dialCode: "+62",  name: "Indonesia" },
    { code: "NZ", dialCode: "+64",  name: "New Zealand" },
    { code: "SA", dialCode: "+966", name: "Saudi Arabia" },
    { code: "QA", dialCode: "+974", name: "Qatar" },
    { code: "KW", dialCode: "+965", name: "Kuwait" },
];

const MAX_CANDIDATE_ROWS = 2;
const MAX_FILES = 20;
const ALLOWED_EXTENSIONS = [".pdf", ".docx"];
const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const filterValidFiles = (files) =>
    files.filter((file) => {
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
    });

function FileTypeIcon({ name }) {
    const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
    const isPdf = ext === ".pdf";
    return (
        <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            background: isPdf ? "#FFF0F4" : "#F0F4FF",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "9px", fontWeight: 700,
            color: isPdf ? "#F75891" : "#4A6EE0",
            border: isPdf ? "1px solid #F7589133" : "1px solid #4A6EE033",
            flexShrink: 0,
        }}>
            {isPdf ? "PDF" : "DOC"}
        </div>
    );
}

function ProgressItem({ item }) {
    const { file_name, status, progress, ui_message } = item;
    const isSuccess = status === "Completed" || status === "Extracted" || status === "Done" || status === "Success";
    const isFailed = status?.toLowerCase().includes("fail") || status?.toLowerCase().includes("error") || status?.toLowerCase().includes("missing");
    const color = isSuccess ? "#22C55E" : isFailed ? "#EF4444" : "#F75891";
    const barWidth = isSuccess ? "100%" : isFailed ? "100%" : `${progress || 0}%`;

    return (
        <div style={{
            padding: "12px",
            borderRadius: "8px",
            background: "#F7F9FC",
            border: `1px solid ${isFailed ? "#FECACA" : isSuccess ? "#BBF7D0" : "#E4E9F0"}`,
            marginBottom: "8px",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <FileTypeIcon name={file_name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                        margin: 0, fontSize: "13px", fontWeight: 500, color: "#2D3748",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={file_name}>
                        {file_name}
                    </p>
                    <p style={{ margin: 0, fontSize: "11px", color: isFailed ? "#EF4444" : isSuccess ? "#16A34A" : "#8A9199" }}>
                        {ui_message || status}
                    </p>
                </div>
                <span style={{ fontSize: "11px", fontWeight: 600, color, flexShrink: 0 }}>
                    {isSuccess ? "Done" : isFailed ? "Failed" : `${progress || 0}%`}
                </span>
            </div>
            <div style={{ height: "4px", borderRadius: "2px", background: "#E4E9F0", overflow: "hidden" }}>
                <div style={{
                    height: "100%",
                    width: barWidth,
                    background: color,
                    transition: "width 0.4s ease",
                    borderRadius: "2px",
                }} />
            </div>
            <div style={{ marginTop: "4px", textAlign: "right" }}>
                <span style={{ fontSize: "11px", color, fontWeight: 500 }}>{status}</span>
            </div>
        </div>
    );
}

function CountryCodeSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    border: "none", borderRight: "1px solid #E4E9F0",
                    background: "#F7F9FC", padding: "0 8px",
                    height: "100%", fontSize: "13px", color: "#525B66",
                    cursor: "pointer", outline: "none",
                    display: "flex", alignItems: "center", gap: "4px",
                    minWidth: "68px",
                }}
            >
                {value} <span style={{ fontSize: "9px", marginTop: "1px" }}>▾</span>
            </button>
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0,
                    background: "#fff", border: "1px solid #E4E9F0",
                    borderRadius: "6px", zIndex: 200,
                    maxHeight: "200px", overflowY: "auto",
                    minWidth: "170px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}>
                    {COUNTRY_CODES.map((c) => (
                        <button
                            key={c.code}
                            type="button"
                            onClick={() => { onChange(c.dialCode); setOpen(false); }}
                            style={{
                                display: "block", width: "100%",
                                padding: "7px 12px", border: "none",
                                background: c.dialCode === value ? "#FFF5F9" : "transparent",
                                textAlign: "left", cursor: "pointer",
                                fontSize: "13px", color: "#2D3748",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {c.dialCode} — {c.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stage4InviteCandidates({ jobId, onBack, transitioning, transitionMessage, resumeUrl, resumeFilename }) {
    const effectiveResumeUrl = resumeUrl || DEFAULT_RESUME_URL;
    const effectiveResumeFilename = resumeFilename || DEFAULT_RESUME_FILENAME;
    const userEmail = localStorage.getItem('email') || 'user@example.com';
    const userName = localStorage.getItem('name') || localStorage.getItem('fullName') || userEmail;

    const [rows, setRows] = useState([{ email: userEmail, dialCode: '+91', number: '' }]);
    const [screeningLoading, setScreeningLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Check if Resume Scoring is enabled from Redux store
    const hiringStages = useSelector((state) => state.jobs.hiringStages);
    const isResumeScoringEnabled = hiringStages?.some(
        (s) => s.hiring_stage_id === RESUME_SCORING_STAGE_ID && s.is_enabled
    );

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    // { [resume_upload_id]: { file_name, status, progress, ui_message } }
    const [uploadItems, setUploadItems] = useState({});
    const [screeningUploadIds, setScreeningUploadIds] = useState([]);
    const [screeningStarted, setScreeningStarted] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("error");

    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const dragCounter = useRef(0);

    // Refs always hold the latest values — safe to read inside the WS handler closure
    const screeningUploadIdsRef = useRef([]);
    const screeningStartedRef = useRef(false);
    const jobIdRef = useRef(jobId);
    const isResumeScoringEnabledRef = useRef(isResumeScoringEnabled);
    const navigateRef = useRef(navigate);
    const showSnackRef = useRef(null);

    const wsSocketRef = useRef(null);
    const wsReconnectTimerRef = useRef(null);
    const wsReconnectAttemptsRef = useRef(0);
    const wsIntentionalCloseRef = useRef(false);
    const WS_MAX_RECONNECT = 10;

    useEffect(() => { screeningUploadIdsRef.current = screeningUploadIds; }, [screeningUploadIds]);
    useEffect(() => { screeningStartedRef.current = screeningStarted; }, [screeningStarted]);
    useEffect(() => { jobIdRef.current = jobId; }, [jobId]);
    useEffect(() => { isResumeScoringEnabledRef.current = isResumeScoringEnabled; }, [isResumeScoringEnabled]);
    useEffect(() => { navigateRef.current = navigate; }, [navigate]);
    useEffect(() => {
        dispatch(updateJob(jobId, { job_status_id: "14107328-840b-4ad0-904f-8a2243720f70" }));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenentId');
        const baseUrl = process.env.REACT_APP_WS_URL;

        if (!token || !tenantId || !baseUrl) return;

        wsIntentionalCloseRef.current = false;

        const connect = () => {
            if (wsIntentionalCloseRef.current) return;

            const wsUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
            const socket = new WebSocket(wsUrl);
            wsSocketRef.current = socket;

            socket.onopen = () => {
                wsReconnectAttemptsRef.current = 0;
                socket.send(JSON.stringify({ action: 'join', group: `tenant-${tenantId}` }));
            };

            socket.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    if (parsed.event === 'resume_upload_updated' && parsed.data) {
                        window.dispatchEvent(new CustomEvent('resume_upload_updated', { detail: parsed.data }));
                    }
                } catch (err) {
                    console.error('Error parsing WebSocket message:', err);
                }
            };

            socket.onclose = () => {
                if (wsIntentionalCloseRef.current) return;
                if (wsReconnectAttemptsRef.current >= WS_MAX_RECONNECT) return;
                const delay = Math.min(30000, 1000 * Math.pow(2, wsReconnectAttemptsRef.current));
                wsReconnectAttemptsRef.current += 1;
                wsReconnectTimerRef.current = setTimeout(connect, delay);
            };

            socket.onerror = () => {
                socket.close();
            };
        };

        connect();

        return () => {
            wsIntentionalCloseRef.current = true;
            clearTimeout(wsReconnectTimerRef.current);
            if (wsSocketRef.current) wsSocketRef.current.close();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Listen to WebSocket events and trigger navigation when all screening items settle
    useEffect(() => {
        const handler = (e) => {
            const d = e.detail;
            if (!d?.id) return;

            setUploadItems((prev) => {
                const next = {
                    ...prev,
                    [d.id]: {
                        file_name: d.file_name,
                        status: d.status,
                        progress: d.progress,
                        ui_message: d.ui_message,
                    },
                };

                if (screeningStartedRef.current) {
                    const ids = screeningUploadIdsRef.current;
                    if (ids.length > 0) {
                        const items = ids.map((id) => next[id]).filter(Boolean);
                        if (items.length === ids.length) {
                            const allSettled = items.every((item) =>
                                item.status === "Completed" || item.status === "Extracted" || item.status === "Done" || item.status === "Success" ||
                                item.status?.toLowerCase().includes("fail") || item.status?.toLowerCase().includes("error") || item.status?.toLowerCase().includes("missing")
                            );
                            if (allSettled) {
                                const anySuccess = items.some((item) =>
                                    item.status === "Completed" || item.status === "Extracted" || item.status === "Done" || item.status === "Success"
                                );
                                // Schedule outside the updater so we can call navigate / setState
                                setTimeout(() => {
                                    if (anySuccess) {
                                        navigateRef.current(`/jobs/${jobIdRef.current}/candidates`, {
                                            state: { autoAssignResumeScoring: isResumeScoringEnabledRef.current },
                                        });
                                    } else {
                                        showSnackRef.current?.("All resumes failed to process. Please try again.", "error");
                                        setScreeningStarted(false);
                                        setScreeningUploadIds([]);
                                    }
                                }, 0);
                            }
                        }
                    }
                }

                return next;
            });
        };
        window.addEventListener('resume_upload_updated', handler);
        return () => window.removeEventListener('resume_upload_updated', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const addRow = () => setRows((prev) => [...prev, { email: '', dialCode: '+91', number: '' }]);

    const updateRow = (index, field, value) =>
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

    const removeRow = (index) => {
        if (rows.length <= 1) return; // at least one row must remain
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const showSnack = useCallback((message, severity = "error") => {
        setSnackMessage(message);
        setSnackSeverity(severity);
        setSnackOpen(true);
    }, []);
    showSnackRef.current = showSnack;

    const snackClosed = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackOpen(false);
    };

    // Start Screening is enabled when rows are filled OR files have been uploaded via modal
    const allRowsFilled = rows.every((r) => r.email.trim() !== '' && r.number.trim() !== '');
    const hasSuccessfulUploads = Object.values(uploadItems).some(
        (item) => item.status === "Completed" || item.status === "Extracted" || item.status === "Done" || item.status === "Success"
    );
    const canStartScreening = allRowsFilled || hasSuccessfulUploads;


    const handleStartScreening = async () => {
        if (!canStartScreening) return;

        // Files already uploaded via modal — just navigate
        if (!allRowsFilled) {
            navigate(`/jobs/${jobId}/candidates`, {
                state: { autoAssignResumeScoring: isResumeScoringEnabled },
            });
            return;
        }

        setScreeningLoading(true);
        try {
            const payload = {
                resumes: rows.map((row) => ({
                    url: effectiveResumeUrl,
                    filename: effectiveResumeFilename,
                    name: userName,
                    email: row.email.trim(),
                    phone_number: `${row.dialCode}${row.number.trim()}`,
                })),
                trigger_resume_scoring: !!isResumeScoringEnabled
            };

            const result = await dispatch(uploadResumesFromUrls(jobId, payload));

            if (result?.success) {
                const items = Array.isArray(result.data) ? result.data : [];
                const ids = items.map((item) => item.resume_upload_id).filter(Boolean);
                if (ids.length > 0) {
                    const initial = {};
                    items.forEach((item) => {
                        if (item.resume_upload_id) {
                            initial[item.resume_upload_id] = {
                                file_name: item.filename || item.file_name || "Resume",
                                status: "Uploading",
                                progress: 0,
                                ui_message: `Processing "${item.filename || item.file_name || "Resume"}"…`,
                            };
                        }
                    });
                    setUploadItems((prev) => ({ ...prev, ...initial }));
                    setScreeningUploadIds(ids);
                    setScreeningStarted(true);
                } else {
                    // API returned no IDs — navigate immediately
                    navigate(`/jobs/${jobId}/candidates`, {
                        state: { autoAssignResumeScoring: isResumeScoringEnabled },
                    });
                }
            } else {
                showSnack(result?.message || "Failed to start screening", "error");
            }
        } catch (err) {
            showSnack(err?.message || "Unexpected error occurred", "error");
        } finally {
            setScreeningLoading(false);
        }
    };

    const addFiles = useCallback((incoming) => {
        const valid = filterValidFiles(incoming);
        setFiles((prev) => {
            const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
            const unique = valid.filter((f) => !existing.has(`${f.name}-${f.size}`));
            return [...prev, ...unique].slice(0, MAX_FILES);
        });
    }, []);

    const handleBrowse = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleAddMore = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current += 1;
        if (dragCounter.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

    const handleDrop = async (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        const items = Array.from(e.dataTransfer.items || []);
        const collected = [];

        for (const item of items) {
            if (item.kind !== "file") continue;
            const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
            if (entry) {
                if (entry.isDirectory) {
                    const nested = await readDirectoryFiles(entry);
                    collected.push(...nested);
                } else {
                    const file = item.getAsFile();
                    if (file) collected.push(file);
                }
            } else {
                const file = item.getAsFile();
                if (file) collected.push(file);
            }
        }

        addFiles(collected);
    };

    const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const isItemActive = (item) =>
        item.status !== "Completed" &&
        item.status !== "Extracted" &&
        item.status !== "Done" &&
        item.status !== "Success" &&
        !item.status?.toLowerCase().includes("fail") &&
        !item.status?.toLowerCase().includes("error") &&
        !item.status?.toLowerCase().includes("missing");

    const hasActiveUploads = Object.values(uploadItems).some(isItemActive);

    const openUploadModal = () => {
        setShowUploadModal(true);
        if (hasActiveUploads || Object.keys(uploadItems).length > 0) {
            setModalStep(2);
        } else {
            setModalStep(1);
            setFiles([]);
        }
    };

    const handleUpload = async () => {
        if (!files.length) {
            showSnack("Please select at least one file.");
            return;
        }
        setUploading(true);
        try {
            const result = await dispatch(uploadPdfCandidateFile(jobId, files));
            if (result?.success && Array.isArray(result.data)) {
                const initial = {};
                result.data.forEach((item) => {
                    if (item.resume_upload_id) {
                        initial[item.resume_upload_id] = {
                            file_name: item.filename,
                            status: "Uploading",
                            progress: 0,
                            ui_message: `Uploading "${item.filename}"…`,
                        };
                    }
                });
                setUploadItems((prev) => ({ ...prev, ...initial }));
                setFiles([]);
                setModalStep(2);
                dispatch(fetchCandidatesByJobId(jobId));
            } else {
                showSnack(result?.message || "Upload failed", "error");
            }
        } catch (err) {
            showSnack(err?.message || "Unexpected error occurred", "error");
        } finally {
            setUploading(false);
        }
    };

    const closeModal = () => {
        setShowUploadModal(false);
        setFiles([]);
        dragCounter.current = 0;
        setIsDragging(false);
    };

    if (transitioning) {
        return (
            <div className="cj-stage4" style={{ pointerEvents: 'none' }}>
                <div className="cj-invite-header">
                    <p className="gradient-text-loading">{transitionMessage}</p>
                </div>
                <div className="cj-feature-card-empty-skeleton loading-gradient-border" style={{ height: '300px' }} />
            </div>
        );
    }

    const allItems = Object.entries(uploadItems);

    return (
        <div className="cj-stage4">
            <button className="cj-back-link" onClick={onBack}>
                <FaChevronLeft /> Back to AI Automation Rules
            </button>

            <div className="cj-invite-header">
                <h3 className="cj-invite-title">Invite Candidates</h3>
                <p className="cj-invite-subtitle">
                    Upload CSV for bulk upload, docx or pdf for individual resumes
                </p>

                <div className="cj-upload-btns">
                    <button className="cj-upload-btn" onClick={openUploadModal}>
                        <LuTableProperties size={18} />
                        Upload CSV
                    </button>
                    <button className="cj-upload-btn" onClick={openUploadModal}>
                        <LuFileText size={18} />
                        Upload resume
                    </button>
                </div>
            </div>

            <div className="cj-experience-card">
                <h4 className="cj-exp-title">Experience Gappeo as a candidate</h4>
                <p className="cj-exp-subtitle">
                    Add your mobile. We have prefilled your <br /> email id and created a sample CV.
                </p>

                <div className="cj-candidate-rows">
                    {rows.map((row, index) => (
                        <div key={index} className="cj-candidate-row">
                            <input
                                type="email"
                                className="cj-input"
                                value={row.email}
                                onChange={(e) => updateRow(index, 'email', e.target.value)}
                                placeholder="Email address"
                            />
                            <div className="cj-input" style={{ display: "flex", alignItems: "center", padding: 0, overflow: "visible" }}>
                                <CountryCodeSelect
                                    value={row.dialCode}
                                    onChange={(dialCode) => updateRow(index, 'dialCode', dialCode)}
                                />
                                <input
                                    type="tel"
                                    value={row.number}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^\d\s\-()]/g, '');
                                        updateRow(index, 'number', val);
                                    }}
                                    placeholder="Mobile number"
                                    style={{
                                        border: "none", outline: "none", flex: 1,
                                        padding: "0 10px", fontSize: "13px",
                                        background: "transparent", minWidth: 0,
                                    }}
                                />
                            </div>
                            <div
                                className="cj-cv-icon"
                                style={{ cursor: 'pointer' }}
                                onClick={() => window.open(effectiveResumeUrl, '_blank', 'noopener,noreferrer')}
                            >
                                <LuFileText size={22} />
                                <span>Your CV</span>
                            </div>
                            {rows.length > 1 && (
                                <button
                                    className="cj-remove-row-btn"
                                    onClick={() => removeRow(index)}
                                    title="Remove row"
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#8A9199",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = "#FF1C58")}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                                >
                                    <FaTrash size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {rows.length < MAX_CANDIDATE_ROWS && (
                    <button className="cj-add-row-btn" onClick={addRow}>
                        Add a row
                    </button>
                )}

                {screeningStarted && screeningUploadIds.length > 0 && (
                    <div style={{ marginTop: "16px" }}>
                        <p style={{ margin: "0 0 8px", fontSize: "13px", fontWeight: 600, color: "#525B66" }}>
                            Processing candidates…
                        </p>
                        {screeningUploadIds.map((id) =>
                            uploadItems[id] ? <ProgressItem key={id} item={uploadItems[id]} /> : null
                        )}
                    </div>
                )}
            </div>

            <button
                className={classNames("cj-btn-primary")}
                onClick={handleStartScreening}
                disabled={!canStartScreening || screeningLoading || screeningStarted}
                style={{ opacity: !canStartScreening || screeningLoading || screeningStarted ? 0.5 : 1, cursor: !canStartScreening ? "not-allowed" : "pointer" }}
            >
                {screeningLoading || screeningStarted ? "Processing…" : "Start Screening"} <span className="cj-btn-arrow">→</span>
            </button>

            <button className="cj-cancel-btn" onClick={onBack} disabled={screeningLoading}>
                Cancel
            </button>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackOpen}
                autoHideDuration={3000}
                onClose={snackClosed}
            >
                <Alert onClose={snackClosed} severity={snackSeverity} sx={{ width: "100%" }}>
                    {snackMessage}
                </Alert>
            </Snackbar>

            {showUploadModal && (
                <div className="upload-modal">
                    <div className="modal-content" style={{ maxWidth: "520px", width: "100%" }}>
                        <div className="modal-head">
                            <div />
                            <h3>Upload Resumes</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={closeModal}
                                style={{ cursor: "pointer" }}
                            />
                        </div>

                        {/* ── Step 1: File selection ── */}
                        {modalStep === 1 && (
                            <div style={{ padding: "20px" }}>
                                {files.length === 0 ? (
                                    <div
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            border: isDragging ? "2px dashed #F75891" : "2px dashed #E4E9F0",
                                            borderRadius: "12px",
                                            cursor: "pointer",
                                            minHeight: "180px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "28px 24px",
                                            transition: "border-color 0.2s, background 0.2s",
                                            background: isDragging ? "#FFF5F9" : "#FAFBFC",
                                            marginBottom: "16px",
                                        }}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,.docx"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={handleBrowse}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <img src="/images/upload-icon-pink.svg" alt="" style={{ marginBottom: "8px" }} />
                                        <p style={{ margin: "4px 0" }}>
                                            <strong style={{ fontWeight: 600 }}>Click to upload</strong>{" "}or drag and drop
                                        </p>
                                        <p style={{ color: "#8A9199", fontSize: "13px", margin: "2px 0" }}>
                                            Supported:{" "}
                                            <strong style={{ color: "#525B66" }}>.pdf</strong>,{" "}
                                            <strong style={{ color: "#525B66" }}>.docx</strong>
                                        </p>
                                        <p style={{ color: "#B0B8C1", fontSize: "12px", margin: "2px 0" }}>
                                            Max {MAX_FILES} files
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: "16px" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                            <p style={{ margin: 0, fontWeight: 600, fontSize: "14px", color: "#525B66" }}>
                                                {files.length} file{files.length > 1 ? "s" : ""} selected
                                            </p>
                                            <span style={{ fontSize: "12px", color: "#8A9199" }}>max {MAX_FILES}</span>
                                        </div>
                                        <div style={{ maxHeight: "240px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
                                            {files.map((f, idx) => (
                                                <div key={idx} style={{
                                                    display: "flex", alignItems: "center", gap: "10px",
                                                    padding: "8px 12px", borderRadius: "8px",
                                                    background: "#F7F9FC", border: "1px solid #E4E9F0",
                                                }}>
                                                    <FileTypeIcon name={f.name} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{
                                                            margin: 0, fontSize: "13px", fontWeight: 500, color: "#2D3748",
                                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                        }} title={f.name}>{f.name}</p>
                                                        <p style={{ margin: 0, fontSize: "11px", color: "#8A9199" }}>
                                                            {(f.size / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#8A9199", fontSize: "18px", lineHeight: 1, padding: "0 2px" }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.color = "#FF1C58")}
                                                        onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                                                        title="Remove"
                                                    >×</button>
                                                </div>
                                            ))}
                                            {files.length < MAX_FILES && (
                                                <>
                                                    <input
                                                        ref={addMoreInputRef}
                                                        type="file"
                                                        accept=".pdf,.docx"
                                                        multiple
                                                        style={{ display: "none" }}
                                                        onChange={handleAddMore}
                                                    />
                                                    <button
                                                        className="upload-btn"
                                                        onClick={() => addMoreInputRef.current?.click()}
                                                        style={{ border: "1px solid #111928", color: "#111928", backgroundColor: "transparent", margin: 0 }}
                                                    >
                                                        + Add more files
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                    {allItems.length > 0 && (
                                        <button
                                            className="upload-btn"
                                            onClick={() => setModalStep(2)}
                                            style={{ border: "1px solid #111928", color: "#111928", backgroundColor: "transparent" }}
                                        >
                                            View Progress
                                        </button>
                                    )}
                                    <button
                                        className="upload-btn"
                                        onClick={handleUpload}
                                        disabled={files.length === 0 || uploading}
                                        style={{ opacity: files.length === 0 || uploading ? 0.5 : 1 }}
                                    >
                                        {uploading ? "Uploading…" : "Upload Candidates"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Step 2: Progress ── */}
                        {modalStep === 2 && (
                            <div style={{ padding: "20px" }}>
                                <div style={{ maxHeight: "380px", overflowY: "auto" }}>
                                    {allItems.length === 0 ? (
                                        <p style={{ textAlign: "center", color: "#8A9199", fontSize: "14px", padding: "24px 0" }}>
                                            No uploads yet.
                                        </p>
                                    ) : (
                                        allItems.map(([id, item]) => (
                                            <ProgressItem key={id} item={item} />
                                        ))
                                    )}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginTop: "16px" }}>
                                    <button
                                        className="upload-btn"
                                        onClick={() => setModalStep(1)}
                                        style={{ border: "1px solid #111928", color: "#111928", backgroundColor: "transparent" }}
                                    >
                                        + Add More Files
                                    </button>
                                    <button className="upload-btn" onClick={closeModal}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Stage4InviteCandidates
